/**
 * Configuration change history — /v3/admin/config-changes (pcb-retail-api).
 * Bill 2026-10-08: every change to settings, disclosures and configuration is
 * logged (who, when, before → after, why) and can be restored.
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface ConfigChangeRow {
  changeId: number;
  changedAt: string;
  actor: string;
  app: string | null;
  source: string | null;
  tableName: string;
  entityType: string | null;
  keyJson: string | null;
  operation: 'Insert' | 'Update' | 'Delete' | 'SeedSql';
  oldValuesJson: string | null;
  newValuesJson: string | null;
  reason: string | null;
  restoresChangeId: number | null;
  batchId: string;
  restoredBy: ReadonlyArray<{ changeId: number; actor: string; changedAt: string }>;
  canRestore: boolean;
}

export interface ConfigChangeQuery {
  table?: string;
  actor?: string;
  op?: string;
  q?: string;
  from?: string;
  to?: string;
  before?: number;
  take?: number;
}

export interface ConfigRestoreResult {
  success: boolean;
  code: string;
  message: string;
  conflicts?: ReadonlyArray<{ column: string; expectedJson: string | null; currentJson: string | null }> | null;
}

export class ConfigChangesApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(query: ConfigChangeQuery = {}, opts?: RequestOptions): Promise<{ success: boolean; data: ReadonlyArray<ConfigChangeRow>; more: boolean; nextBefore: number | null }> {
    const q: Record<string, string> = {};
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') q[k] = String(v);
    return this.http.get('/v3/admin/config-changes', { ...opts, query: q });
  }

  public tables(opts?: RequestOptions): Promise<{ success: boolean; data: ReadonlyArray<{ tableName: string; changes: number; lastChangedAt: string }> }> {
    return this.http.get('/v3/admin/config-changes/tables', { ...opts });
  }

  /** Undo one change. 409 (thrown) when the row changed since - retry with force after the person confirms. */
  public restore(changeId: number, body: { reason: string; force?: boolean }, opts?: RequestOptions): Promise<ConfigRestoreResult> {
    return this.http.post(`/v3/admin/config-changes/${changeId}/restore`, { ...opts, body });
  }
}
